/**
 * Labels for a curated walk's length and time (Kurtis 2026-10-19): lengths rounded the way people say them ("about
 * 1 km", "just over 1 km", "nearly 1.5 km", never "1186 m"), and time as a range from walking through (~2.5 mph) to a
 * slow stroll (~1 mph). Stops are the walker's own business.
 *
 * Pure; the keys are under tripPlanner:mapTab.walks.
 */

const M_PER_MILE = 1609;

// → { key, params } for t(`mapTab.walks.length.${key}`, params)
export function walkLengthLabel(meters) {
  if (!Number.isFinite(meters) || meters <= 0) return null;
  if (meters < 950) return { key: 'aboutM', params: { m: Math.max(100, Math.round(meters / 100) * 100) } };
  const km = meters / 1000;
  const base = Math.floor(km * 2) / 2;
  const frac = km - base;
  const fmt = x => String(x).replace(/\.0$/, '');
  if (frac < 0.1) return { key: 'aboutKm', params: { km: fmt(base) } };
  if (frac < 0.3) return { key: 'justOverKm', params: { km: fmt(base) } };
  return { key: 'nearlyKm', params: { km: fmt(base + 0.5) } };
}

// → { from, to } minutes, rounded to 5, walking through to strolling
export function walkDurationRange(meters) {
  if (!Number.isFinite(meters) || meters <= 0) return null;
  const at = mph => Math.round((meters / M_PER_MILE / mph) * 60 / 5) * 5;
  return { from: Math.max(5, at(2.5)), to: Math.max(10, at(1)) };
}

// The minutes a walk is planned at on a day: the middle of its range, to 5 minutes. 60 when its length is unknown.
export function walkPlannedMinutes(meters) {
  const range = walkDurationRange(meters);
  return range ? Math.round((range.from + range.to) / 2 / 5) * 5 : 60;
}

/**
 * The places a walk passes, in walking order, for the card's photo strip: the start anchor, the stops (numbered, in
 * their own order — never re-sorted, since a loop's nearest line point can jump back), each landmark slotted between
 * the two placed stops whose line positions (`path_i`, from the API) it falls between — stops with no place are
 * skipped over, the landmark sits next to the nearer of the two — then the end anchor. A place listed
 * twice (a start that is also stop 1) shows once, captioned with both. Anchors that are not our places are left out
 * (the start/end line names them).
 *
 * → [{ key, name, attractionId, photoUrl, number, role: 'start' | 'end' | null, lat, lng }]
 */
export function walkPlaces(walk) {
  const stops = (walk.stops || []).filter(s => s.name).map((s, i) => ({ name: s.name, place: s.place, number: i + 1, at: s.path_i }));
  const landmarks = (walk.landmarks || []).filter(l => l.name).map(l => ({ name: l.name, place: l, at: l.path_i }));
  const slots = stops.map(() => []);
  const before = [];
  const after = [];
  // Pairs of consecutive stops that have a line position, skipping the ones that don't (Marrakech's souk lanes are
  // stops, not our places). A landmark goes just after the nearer stop of its pair, so one level with the next stop
  // sits right before it; with every stop placed that is the slot between the two.
  const placed = stops.map((s, i) => i).filter(i => Number.isFinite(stops[i].at));
  const pairs = placed.slice(1).map((j, n) => [placed[n], j]);
  for (const l of landmarks) {
    const pair = Number.isFinite(l.at)
      ? pairs.find(([i, j]) => l.at >= Math.min(stops[i].at, stops[j].at) && l.at <= Math.max(stops[i].at, stops[j].at))
      : null;
    if (pair) {
      const [i, j] = pair;
      slots[Math.abs(l.at - stops[i].at) <= Math.abs(stops[j].at - l.at) ? i : j - 1].push(l);
    } else if (placed.length && Number.isFinite(l.at) && l.at < stops[placed[0]].at) {
      (placed[0] > 0 ? slots[placed[0] - 1] : before).push(l);
    } else after.push(l);
  }
  const byAt = (a, b) => (Number.isFinite(a.at) ? a.at : Infinity) - (Number.isFinite(b.at) ? b.at : Infinity);
  after.sort(byAt);
  slots.forEach(s => s.sort(byAt));
  before.sort(byAt);
  const seq = [
    walk.start_anchor?.place ? { name: walk.start_anchor.name, place: walk.start_anchor.place, role: 'start' } : null,
    ...before,
    ...stops.flatMap((s, i) => [s, ...slots[i]]),
    ...after,
    walk.end_anchor?.place ? { name: walk.end_anchor.name, place: walk.end_anchor.place, role: 'end' } : null
  ].filter(Boolean);
  const out = [];
  const seen = new Map();
  for (const p of seq) {
    const id = p.place?.attraction_id || null;
    const prev = id ? seen.get(id) : null;
    if (prev) {
      // The anchor's role moves onto the stop or landmark it duplicates; the stop's name and number win.
      if (p.role && !prev.role) prev.role = p.role;
      if (p.number && !prev.number) Object.assign(prev, { name: p.name, number: p.number });
      continue;
    }
    const item = {
      key: id || `${p.role || 'x'}:${p.name}`, name: p.name, attractionId: id, photoUrl: p.place?.photo_url || null,
      number: p.number || null, role: p.role || null,
      lat: Number.isFinite(p.place?.lat) ? p.place.lat : null, lng: Number.isFinite(p.place?.lng) ? p.place.lng : null
    };
    if (id) seen.set(id, item);
    out.push(item);
  }
  return out;
}
